import { Button } from "@/components/ui/button";
import { AionSectionCard } from "@/components/aion/AionSectionCard";

const QUICK_ACTIONS_BY_ROLE = {
  PLATFORM_ADMIN: [
    { tab: "customers", label: "+ Neuer Kunde", testId: "operator-home-create-customer-button" },
    { tab: "employees", label: "+ Neuer Mitarbeiter", testId: "operator-home-create-employee-button" },
    { tab: "installations", label: "+ Neue Installation", testId: "operator-home-create-installation-button" },
  ],
  MANAGER: [
    { tab: "customers", label: "+ Neuer Kunde", testId: "operator-home-create-customer-button" },
    { tab: "employees", label: "+ Neuer Mitarbeiter", testId: "operator-home-create-employee-button" },
    { tab: "installations", label: "+ Neue Installation", testId: "operator-home-create-installation-button" },
  ],
  SALES_EMPLOYEE: [
    { tab: "customers", label: "Kunden", testId: "operator-home-sales-customers-button" },
    { tab: "users", label: "Benutzer", testId: "operator-home-sales-users-button" },
  ],
  INSTALLER: [
    { tab: "customers", label: "+ Neuer Kunde", testId: "operator-home-installer-create-customer-button" },
    { tab: "onboarding", label: "+ Installation starten", testId: "operator-home-installer-start-installation-button" },
    { tab: "hardware", label: "+ Gerät scannen", testId: "operator-home-installer-scan-device-button" },
  ],
  TECHNICIAN: [
    { tab: "installations", label: "Installationen", testId: "operator-home-technician-installations-button" },
    { tab: "hardware", label: "Hardware", testId: "operator-home-technician-hardware-button" },
  ],
  SUPPORT: [
    { tab: "users", label: "Benutzer", testId: "operator-home-support-users-button" },
    { tab: "system", label: "Systemstatus", testId: "operator-home-support-system-button" },
  ],
  CUSTOMER_OWNER: [
    { tab: "onboarding", label: "Handover prüfen", testId: "operator-home-customer-handover-button" },
    { tab: "users", label: "Benutzer verwalten", testId: "operator-home-customer-users-button" },
  ],
  CUSTOMER_ADMIN: [
    { tab: "onboarding", label: "Handover prüfen", testId: "operator-home-customer-handover-button" },
    { tab: "users", label: "Benutzer verwalten", testId: "operator-home-customer-users-button" },
  ],
};

export const OperatorHomePanel = ({ dashboard = null, hardware = [], home = null, installations = [], operatorRole = "PLATFORM_ADMIN", onRefresh, onSelectTab }) => {
  const counts = home?.counts || dashboard?.counts || {};
  const profile = home?.employee || {};
  const pipeline = home?.pipeline || [];
  const activity = home?.recent_activity || dashboard?.recent_activity || [];
  const healthEntries = Object.entries(dashboard?.health || {});
  const normalizedOperatorRole = String(operatorRole || "").toUpperCase();
  const quickActions = QUICK_ACTIONS_BY_ROLE[normalizedOperatorRole] || [];

  return (
    <div className="aion-admin-stack" data-testid="operator-home-panel">
      <AionSectionCard
        title="Employee Home"
        eyebrow="FIELD OPS"
        subtitle="Role-aware operator home for assigned customers, open installations, handovers, and recent platform actions."
        action={<Button variant="outline" onClick={() => onRefresh?.()} data-testid="operator-home-refresh-button">Refresh</Button>}
        testId="operator-home-card"
      >
        <div className="aion-compact-status-grid" data-testid="operator-home-stats-grid">
          <div className="aion-compact-status-box" data-testid="operator-home-role"><span>Role</span><strong>{profile.account_role || operatorRole}</strong></div>
          <div className="aion-compact-status-box" data-testid="operator-home-status"><span>Status</span><strong>{profile.status || "ACTIVE"}</strong></div>
          <div className="aion-compact-status-box" data-testid="operator-home-region"><span>Region</span><strong>{profile.assigned_region || "Assigned customers"}</strong></div>
          <div className="aion-compact-status-box" data-testid="operator-home-customers"><span>Customers</span><strong>{counts.customers ?? 0}</strong></div>
          <div className="aion-compact-status-box" data-testid="operator-home-installations"><span>Installations</span><strong>{counts.installations ?? installations.length}</strong></div>
          <div className="aion-compact-status-box" data-testid="operator-home-ready"><span>Ready for handover</span><strong>{counts.ready_for_handover ?? 0}</strong></div>
        </div>
        <div className="aion-inline-actions" data-testid="operator-home-quick-actions">
          {quickActions.map((action, index) => (
            <Button
              key={normalizedOperatorRole + "-" + action.tab + "-" + action.testId}
              variant={index === 0 ? "default" : "outline"}
              onClick={() => onSelectTab?.(action.tab)}
              data-testid={action.testId}
            >
              {action.label}
            </Button>
          ))}
        </div>
      </AionSectionCard>

      <div className="aion-admin-layout" data-testid="operator-home-layout">
        <AionSectionCard title="Priority Pipeline" eyebrow="ONBOARDING" subtitle="Installations in testing, handover, and live activation stay visible here." testId="operator-home-pipeline-card">
          <div className="aion-list" data-testid="operator-home-pipeline-list">
            {pipeline.length ? pipeline.map((item) => (
              <div className="aion-list-row" key={item.installation_id || item.id} data-testid={`operator-home-pipeline-item-${item.installation_id || item.id}`}>
                <div className="aion-list-copy">
                  <strong>{item.name}</strong>
                  <small>{item.lifecycle_stage || item.status} · {item.location_name || "No location"}</small>
                  <small>{item.installation_type} · Core {item.core_id || "—"} · Health {item.health_status || "—"}</small>
                </div>
              </div>
            )) : <div className="aion-note" data-testid="operator-home-pipeline-empty">No active installation pipeline for this scope.</div>}
          </div>
        </AionSectionCard>

        <AionSectionCard title="Recent Activity" eyebrow="AUDIT" subtitle="Every employee, onboarding, and hardware action lands in the same platform audit stream." testId="operator-home-activity-card">
          <div className="aion-list" data-testid="operator-home-activity-list">
            {activity.length ? activity.map((entry, index) => (
              <div className="aion-list-row" key={`${entry.timestamp || index}-${entry.action || entry.event_type || index}`} data-testid={`operator-home-activity-item-${index}`}>
                <div className="aion-list-copy">
                  <strong>{entry.action || entry.event_type || "activity"}</strong>
                  <small>{entry.status || "success"} · {entry.timestamp || "—"}</small>
                  <small>{entry.target?.name || entry.target?.id || entry.target_id || "target"}</small>
                </div>
              </div>
            )) : <div className="aion-note" data-testid="operator-home-activity-empty">No recent platform activity for this operator yet.</div>}
          </div>
        </AionSectionCard>
      </div>

      {healthEntries.length ? (
        <AionSectionCard title="System Health Snapshot" eyebrow="HEALTH" subtitle="Quick signal check before field work, handover, or live activation." testId="operator-home-health-card">
          <div className="aion-admin-health-grid" data-testid="operator-home-health-grid">
            {healthEntries.map(([label, value]) => (
              <div className="aion-compact-status-box" key={label} data-testid={`operator-home-health-${label.toLowerCase()}`}>
                <span>{label}</span>
                <strong>{value.status}</strong>
                <small>{value.detail}</small>
              </div>
            ))}
          </div>
        </AionSectionCard>
      ) : null}
    </div>
  );
};